/**
 * The operational signal store, as `ObservatorySignalsController` serves it (`api/observatory/signals`).
 *
 * Mirrors `Lighthouse.Api/Contracts/Models/ObservatorySignalModels.cs`. Every enum reaches the wire as its C# member
 * NAME (PascalCase) — the API registers no global enum converter, so the controller maps each state to its name
 * itself. The server serves reason NAMES only; the sentences are the SPA's (components/signals/signalFormat.ts).
 *
 * A newer server may send a value this build does not list. Every helper that switches over one of these unions
 * keeps a default that renders the raw name, never a guessed meaning — so the unions are what this build KNOWS,
 * not a promise about the wire.
 *
 * Signals are raised and auto-resolved by Automaton's detector alone. A person may HANDLE an open signal — "I have got
 * this" (acknowledge), hand it to someone (assign), let go (release) — through `ObservatorySignalHandlingController`
 * (D6b, LighthouseAdmin AND JwtBearer-pinned, audited). Handling never resolves, snoozes or hides anything: an
 * acknowledged signal stays open and counted, and only the detector ends it. There is no resolve and no snooze verb.
 *
 * EVERY handling field is optional or nullable: an API that predates the handling table, or a database where it does
 * not exist (migration AddSignalHandlingAndRunCancellation), serves none of it — and absent reads as "not recorded
 * here", which is unknown, never "unowned".
 */

/** Complete < Partial < Incomplete < Unavailable. Only Complete lets "no open signal" mean "none of these is present". */
export type SignalCoverage = 'Complete' | 'Partial' | 'Incomplete' | 'Unavailable'

export type SignalSeverity = 'Info' | 'Warning' | 'Critical'

/** What a signal's subject key names, and so where its subject lives in Observatory. */
export type SignalSubjectKind = 'EtlHalt' | 'EtlRun' | 'DataCombFinding' | 'SourceConnection' | 'DeadLetterQueue'

/** What happened the last time the detector evaluated a rule. `NotReporting` is read-time only: no row exists. */
export type SignalRuleOutcome = 'Evaluated' | 'Partial' | 'InputUnavailable' | 'Failed' | 'Retired' | 'NotReporting'

/** Whether the detector could see an open signal's subject on its last pass. */
export type SignalObservation = 'Observed' | 'Unobserved'

/** How a signal came to be resolved. Only the detector resolves in this build. */
export type SignalResolution =
  | 'ConditionCleared'
  | 'NoLongerReported'
  | 'SuppressedAtSource'
  | 'SubjectRemoved'
  | 'OutOfScope'

/** A transition in a signal's life. Written on transitions only, never on a refresh. */
export type SignalActivityKind = 'Raised' | 'Unobserved' | 'Reobserved' | 'Resolved'

/**
 * Every `SignalReason` name this build knows, in the C# enum's order.
 *
 * An array, not only a type, so a test can iterate every reason and prove each has a sentence.
 * `DetectorNotReporting` and `RuleNotReporting` are derived by the API at read time and never stored.
 */
export const SIGNAL_REASONS = [
  'InputUnreadable',
  'BrokerUnavailable',
  'QueueUnavailable',
  'ProjectionStale',
  'SourceIdle',
  'NotYetContacted',
  'ObserverRestarted',
  'CombNeverCompleted',
  'CombTableSkipped',
  'CombSweepDisabled',
  'CombColumnNotProfiled',
  'CombRuleUnproven',
  'CombLatestPassFailed',
  'CombPassInProgress',
  'RuleTimedOut',
  'RuleFailed',
  'NotClassified',
  'DetectorNotReporting',
  'RuleNotReporting',
] as const

export type SignalReason = (typeof SIGNAL_REASONS)[number]

/** The severities the list endpoint filters on, most severe first. */
export const SIGNAL_SEVERITIES: readonly SignalSeverity[] = ['Critical', 'Warning', 'Info']

/** The subject kinds the list endpoint filters on, in the rule catalogue's order. */
export const SIGNAL_SUBJECT_KINDS: readonly SignalSubjectKind[] = [
  'EtlHalt', 'EtlRun', 'DataCombFinding', 'SourceConnection', 'DeadLetterQueue',
]

/** The list endpoint's `state` values. `open` is its default. */
export type SignalListState = 'open' | 'resolved' | 'all'

export const SIGNAL_LIST_STATES: readonly SignalListState[] = ['open', 'resolved', 'all']

/** `ObservatorySignalsController.DefaultTake` — omitted from the query string when asked for. */
export const SIGNAL_DEFAULT_TAKE = 50

/** `ObservatorySignalsController.MaxTake`. A larger `take` is REFUSED with a 400, never clamped. */
export const SIGNAL_MAX_TAKE = 200

/** Who has an occurrence, as its latest handling act left it. */
export type SignalHandlingState = 'Unowned' | 'Assigned' | 'Acknowledged'

/** One handling act. `Released` leaves the signal Unowned. */
export type SignalHandlingAction = 'Acknowledged' | 'Assigned' | 'Released'

/**
 * The list endpoint's `handling` values (`ObservatorySignalsController.HandlingFilters`). `attention` = unowned, or
 * assigned and not acknowledged, or owned but changed since handled. An array so the URL parser and its test iterate it.
 */
export const SIGNAL_HANDLING_FILTERS = ['unowned', 'assigned', 'acknowledged', 'mine', 'attention'] as const

export type SignalHandlingFilter = (typeof SIGNAL_HANDLING_FILTERS)[number]

/** The signal's values as they stood when a person acted. */
export interface SignalHandlingSnapshot {
  observedValue: string | null
  conditionSince: string | null
  evidenceAsOf: string | null
  /** `Observed` or `Unobserved`, as stored at the act. */
  observation: SignalObservation | string
}

/**
 * One occurrence's handling, as its latest act left it. Names only, never user ids, and the names go through the
 * server's administrator disclosure: a caller who does not pass LighthouseAdmin gets null names and
 * `namesWithheld: true`.
 */
export interface SignalHandling {
  state: SignalHandlingState
  /** The latest act's revision; 0 when nobody ever acted. A write sends it back as `expectedRevision`. */
  revision: number
  /** Null when nobody ever acted. */
  lastAction: SignalHandlingAction | null
  /** When the latest act was recorded; null when nobody ever acted. */
  since: string | null
  /** The owner's display name as stamped at the act; null when unowned, unnamed, or withheld. */
  ownerName: string | null
  /** Who did the latest act; null when never handled, unnamed, or withheld. */
  byName: string | null
  namesWithheld: boolean
  /** The caller is the owner. Computed by the server against the caller's own id. */
  ownedByCaller: boolean
  /** Owned, and the condition moved after the act, so the act does not vouch for what the signal says now. */
  changedSinceHandled: boolean
  /** What the signal said at the latest act; null when nobody ever acted. */
  whenHandled: SignalHandlingSnapshot | null
}

/** One act of an occurrence's handling history. */
export interface SignalHandlingAct {
  revision: number
  action: SignalHandlingAction
  at: string
  byName: string | null
  /** Who owned it after the act; null when released, unnamed or withheld. */
  ownerName: string | null
  namesWithheld: boolean
  whenHandled: SignalHandlingSnapshot
}

/** The occurrence of the same rule and subject before this one, and how its handling ended. */
export interface SignalPreviousOccurrence {
  id: string
  resolvedAt: string | null
  /** A SignalResolution name. */
  resolution: SignalResolution | string | null
  /** Its handling when it was resolved; null when the handling table does not exist here. */
  finalState: SignalHandlingState | null
  /** Its owner at resolution, by display name; null when unowned, unnamed or withheld. */
  finalOwnerName: string | null
  namesWithheld: boolean
}

/** `POST …/acknowledge|assign|release`: what the act did. `changed: false` = nothing was written or audited. */
export interface SignalHandlingResult {
  changed: boolean
  signalId: string
  isOpen: boolean
  handling: SignalHandling
}

/** One person the signal can be handed to: someone the audit trail saw performing an Observatory write. */
export interface SignalAssignee {
  userId: string
  name: string
  lastActedAt: string
}

/** `GET …/assignees` (LighthouseAdmin AND JwtBearer-pinned). The caller is never listed. */
export interface SignalAssigneeList {
  windowDays: number
  /** Newest act first, at most 200. */
  items: SignalAssignee[]
  /** Every known principal in the window, uncapped. */
  totalCount: number
}

/** Whether a detector is running, as its rule rows say. */
export interface SignalDetector {
  /** The newest evaluation across the expected rules; null when none has run. */
  lastEvaluatedAt: string | null
  evaluatedByHost: string | null
  /** No rule row exists: no detector has ever run against this database. Read this before `isStale`. */
  hasNeverRun: boolean
  /** Rows exist, but none was evaluated within `staleWindowMinutes`. */
  isStale: boolean
  staleWindowMinutes: number
}

export interface SignalRuleEvaluation {
  outcome: SignalRuleOutcome
  /** A SignalReason name. */
  reason: SignalReason | null
  isStale: boolean
  lastEvaluatedAt: string | null
  lastSucceededAt: string | null
  /** The input's own timestamp, e.g. the Data Comb pass the rule read. */
  inputAsOf: string | null
  subjectsObserved: number | null
  subjectsUnobserved: number | null
  evaluatedByHost: string | null
}

/** One rule of the catalogue, as the detector published it. Every text is null until a detector has published it. */
export interface SignalRule {
  ruleId: string
  title: string | null
  severity: SignalSeverity
  subjectKind: SignalSubjectKind
  trigger: string | null
  threshold: string | null
  autoResolve: string | null
  /** What one open signal stands for: the subject each occurrence is keyed by (a run, a finding target, a connector, a queue). */
  dedupeKey: string | null
  limits: string | null
  openCount: number
  /** Open signals not confirmed on the last pass — all of them while the detector is not reporting. */
  openUnconfirmedCount: number
  /** Subjects in the condition deliberately not signalled (stale runs older than the resume window). */
  excludedCount: number | null
  evaluation: SignalRuleEvaluation
}

/** `GET api/observatory/signals/summary`. Answers 200 with `storeProvisioned: false` before the migration. */
export interface SignalSummary {
  /** False when the signal tables do not exist in this database (migration AddOperationalSignals). */
  storeProvisioned: boolean
  detector: SignalDetector
  coverage: SignalCoverage
  /** The same, over the Critical rules only — what the Critical tile may claim. */
  criticalCoverage: SignalCoverage
  /** Null EXACTLY when `coverage` is Unavailable. */
  openCount: number | null
  openUnconfirmedCount: number | null
  openCriticalCount: number | null
  openCriticalUnconfirmedCount: number | null
  /**
   * Whether the handling table exists (migration AddSignalHandlingAndRunCancellation). Null when the store itself
   * does not; absent from an older API. Only `true` lets the Inbox offer the handling filter.
   */
  handlingProvisioned?: boolean | null
  /**
   * Open signals someone acknowledged. A PART of `openCount`, never subtracted from it: an acknowledged signal is
   * still open. Null when `openCount` is, or handling is not provisioned. No unowned count is served, on purpose.
   */
  openAcknowledgedCount?: number | null
  /** Open signals handed to an owner who has not acknowledged. */
  openAssignedCount?: number | null
  /** Open signals that are owned but whose condition moved after the act. */
  openChangedSinceHandledCount?: number | null
  openCriticalAcknowledgedCount?: number | null
  openCriticalAssignedCount?: number | null
  /** etl-halt-active, etl-run-stale, comb-critical-finding, source-unreachable, dead-letter-backlog, then retired rules. */
  rules: SignalRule[]
}

/** One occurrence of a condition. */
export interface Signal {
  id: string
  ruleId: string
  ruleTitle: string | null
  severity: SignalSeverity
  subjectKind: SignalSubjectKind
  subjectKey: string
  subjectLabel: string
  /** The subject's row id where it has one: the EtlRun for a halt or stale run, the finding for a comb signal. */
  subjectRefId: string | null
  /** When the condition itself began, as its input recorded it; null when the input records no start. */
  conditionSince: string | null
  firstObservedAt: string
  /** The detector's clock: the last pass that found the condition. */
  lastCheckedAt: string
  /** The input's own timestamp for that evidence. */
  evidenceAsOf: string | null
  observation: SignalObservation
  unobservedSince: string | null
  /** A SignalReason name. `DetectorNotReporting` and `RuleNotReporting` are derived by the API and never stored. */
  unobservedReason: SignalReason | null
  observedValue: string | null
  threshold: string | null
  isOpen: boolean
  resolvedAt: string | null
  resolution: SignalResolution | null
  /**
   * Who has this occurrence. Null when the handling table does not exist here, absent from an older API — both
   * unknown, which is NOT unowned (`state: 'Unowned'`, `revision: 0`). On a resolved occurrence, the final handling.
   */
  handling?: SignalHandling | null
}

export interface SignalActivity {
  at: string
  kind: SignalActivityKind
  /** A SignalReason name for Unobserved, a SignalResolution name for Resolved, else null. */
  reason: string | null
  host: string | null
  observedValue: string | null
}

/** `GET api/observatory/signals/{id}`: one signal and its transitions, chronological. 404 when missing. */
export interface SignalDetail extends Signal {
  activity: SignalActivity[]
  /** Every act on this occurrence, in revision order. Null (or absent) when the handling table does not exist here. */
  handlingHistory?: SignalHandlingAct[] | null
  /** The occurrence before this one of the same rule and subject; null when there is none. A re-raise starts unowned. */
  previousOccurrence?: SignalPreviousOccurrence | null
}

/** `GET api/observatory/signals`: one page. */
export interface SignalList {
  items: Signal[]
  /** Every match, uncapped — not the page size. */
  totalCount: number
  skip: number
  take: number
  /** False when no detector has evaluated within the stale window; every open item is then served Unobserved. */
  detectorReporting: boolean
}

/** What the list endpoint is asked. An absent field is the endpoint's default, and is not sent. */
export interface SignalListQuery {
  state?: SignalListState
  severity?: SignalSeverity
  ruleId?: string
  subjectKind?: SignalSubjectKind
  /** Only signals the detector could not confirm. */
  observation?: 'unobserved'
  /** Who has them. A 503 when the handling table is missing, so the Inbox sends it only when the summary says it exists. */
  handling?: SignalHandlingFilter
  skip?: number
  take?: number
}
